import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Card } from '@/components/ui/Card';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { ExcelUploader } from '@/components/import/ExcelUploader';
import { useCurrentFarm } from '@/hooks/useCurrentFarm';
import {
  Upload,
  FileSpreadsheet,
  CheckCircle,
  XCircle,
  Clock,
  AlertCircle,
  ChevronRight,
  RefreshCw,
} from 'lucide-react';
import { format } from 'date-fns';
import { RoleGuard } from '@/components/layout/RoleGuard';

export const Route = createFileRoute('/_app/import/')({
  component: () => (
    <RoleGuard allowedRoles={['owner', 'supervisor', 'operator']}>
      <ImportPage />
    </RoleGuard>
  ),
});

interface ImportJob {
  id: string;
  filename: string;
  status: string;
  sheets_found: number;
  total_rows: number;
  valid_rows: number;
  error_rows: number;
  created_at: string;
  processing_completed_at: string | null;
}

const STATUS_CONFIG: Record<string, { icon: typeof CheckCircle; color: string; bg: string }> = {
  pending: { icon: Clock, color: 'text-gray-500', bg: 'bg-gray-100' },
  processing: { icon: RefreshCw, color: 'text-blue-500', bg: 'bg-blue-100' },
  ready_for_review: { icon: AlertCircle, color: 'text-amber-500', bg: 'bg-amber-100' },
  importing: { icon: RefreshCw, color: 'text-blue-500', bg: 'bg-blue-100' },
  completed: { icon: CheckCircle, color: 'text-green-500', bg: 'bg-green-100' },
  failed: { icon: XCircle, color: 'text-red-500', bg: 'bg-red-100' },
  cancelled: { icon: XCircle, color: 'text-gray-400', bg: 'bg-gray-100' },
};

function ImportPage() {
  const { farmId, isLoading: farmLoading } = useCurrentFarm();

  const {
    data: jobs,
    isLoading,
    error,
    refetch,
  } = useQuery<ImportJob[]>({
    queryKey: ['import', 'jobs', farmId],
    queryFn: () => api.get(`/api/v1/farms/${farmId}/import/jobs`),
    refetchInterval: 5000, // Poll every 5s for status updates
    enabled: !!farmId,
  });

  const handleUploadComplete = () => {
    refetch();
  };

  if (!farmId) {
    return farmLoading ? <div className="flex justify-center py-12">Loading...</div> : null;
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Excel Import</h1>
        <p className="mt-1 text-sm text-gray-500">
          Import historical data from your Excel logbooks
        </p>
      </div>

      {/* Upload Section */}
      <Card>
        <Card.Header>
          <div className="flex items-center gap-2">
            <Upload className="h-5 w-5 text-primary-500" />
            <h2 className="font-semibold">Upload Excel File</h2>
          </div>
        </Card.Header>
        <Card.Body>
          <ExcelUploader farmId={farmId} onUploadComplete={handleUploadComplete} />
        </Card.Body>
      </Card>

      {/* Import Jobs List */}
      <Card>
        <Card.Header>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FileSpreadsheet className="h-5 w-5 text-green-500" />
              <h2 className="font-semibold">Import History</h2>
            </div>
            <button
              onClick={() => refetch()}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              title="Refresh"
            >
              <RefreshCw className="h-4 w-4 text-gray-500" />
            </button>
          </div>
        </Card.Header>
        <Card.Body className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <LoadingSpinner size={24} />
            </div>
          ) : error ? (
            <div className="p-6 text-center text-red-500">
              Failed to load import jobs
            </div>
          ) : !jobs || jobs.length === 0 ? (
            <div className="p-6 text-center text-gray-500">
              No imports yet. Upload an Excel file to get started.
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {jobs.map((job) => (
                <ImportJobRow key={job.id} job={job} />
              ))}
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Help Section */}
      <Card>
        <Card.Header>
          <h2 className="font-semibold">Excel Format Guide</h2>
        </Card.Header>
        <Card.Body className="space-y-4">
          <p className="text-sm text-gray-600">
            Your Excel file should have one sheet per pond. Each sheet should include:
          </p>
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <h3 className="font-medium text-sm mb-2">Water Parameters</h3>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>DATE - Reading date</li>
                <li>TRANSPARENCY AM/PM - Secchi disk (cm)</li>
                <li>pH AM/PM - pH readings</li>
                <li>ALKALINITY - Format: "160/140" for AM/PM</li>
                <li>WEATHER - C (cloudy), S (sunny), R (rainy)</li>
                <li>WATER COLOR - BG, LG, YG codes</li>
                <li>DO - Dissolved oxygen</li>
              </ul>
            </div>
            <div>
              <h3 className="font-medium text-sm mb-2">Feeding Data</h3>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>7AM, 10AM, 1PM, 4PM, 7PM - Feed amounts (kg)</li>
                <li>TRAY CHECK - C4, C3, C2, C1, E</li>
                <li>FEED TYPE - Optional feed code</li>
              </ul>
            </div>
          </div>
        </Card.Body>
      </Card>
    </div>
  );
}

function ImportJobRow({ job }: { job: ImportJob }) {
  const config = STATUS_CONFIG[job.status] || STATUS_CONFIG.pending;
  const StatusIcon = config.icon;
  const needsAction = job.status === 'ready_for_review';

  return (
    <Link
      to="/import/$jobId"
      params={{ jobId: job.id }}
      className="flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
    >
      <div className="flex items-center gap-4">
        <div className={`p-2 rounded-lg ${config.bg}`}>
          <StatusIcon className={`h-5 w-5 ${config.color}`} />
        </div>
        <div>
          <div className="flex items-center gap-2">
            <span className="font-medium text-gray-900">{job.filename}</span>
            {needsAction && (
              <span className="px-2 py-0.5 text-xs font-medium bg-amber-100 text-amber-700 rounded-full">
                Needs Review
              </span>
            )}
          </div>
          <div className="text-sm text-gray-500">
            {job.sheets_found} sheets, {job.total_rows} rows
            {job.error_rows > 0 && (
              <span className="text-red-500 ml-2">
                ({job.error_rows} errors)
              </span>
            )}
          </div>
          <div className="text-xs text-gray-400">
            {format(new Date(job.created_at), 'MMM d, yyyy h:mm a')}
          </div>
        </div>
      </div>
      <ChevronRight className="h-5 w-5 text-gray-400" />
    </Link>
  );
}
